import React, { useEffect, useState } from "react";
import { Maximize2, X } from "lucide-react";

const shots = [
    {
        id: "discovery",
        label: "Email discovery",
        title: "Score email trust before sending a single message.",
        desc: "Pull public emails from business websites, group by domain, and surface duplicates and risky catch-alls at a glance.",
        img: "/assets/screenshots/reachforge-leads-email-discovery.svg",
    },
    {
        id: "timeline",
        label: "Activity timeline",
        title: "Every contact attempt, in one auditable trail.",
        desc: "Track contact-form outcomes, add custom statuses, and highlight businesses by where they sit in your workflow.",
        img: "/assets/screenshots/reachforge-leads-activity-timeline.svg",
    },
    {
        id: "editor",
        label: "Email editor",
        title: "A rich editor that respects your SMTP.",
        desc: "Compose outreach, pick recipients, send a test, then ship through your own SMTP provider — with quota awareness.",
        img: "/assets/screenshots/reachforge-leads-rich-email-editor.svg",
    },
    {
        id: "suppression",
        label: "Suppression list",
        title: "Opt-outs that actually stay opted-out.",
        desc: "Local and remote suppression entries are checked before every send. No accidental re-contacts.",
        img: "/assets/screenshots/reachforge-leads-suppression-list.svg",
    },
];

export default function Showcase() {
    const [active, setActive] = useState(shots[0].id);
    const [lightbox, setLightbox] = useState(null); // shot object or null
    const current = shots.find((s) => s.id === active);

    // Close on Escape + lock body scroll while open
    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(null);
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKey);
        };
    }, [lightbox]);

    return (
        <section
            id="showcase"
            data-testid="showcase-section"
            className="border-b border-border bg-surface"
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <span className="overline">In-app showcase</span>
                        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] text-ink max-w-2xl">
                            A desktop workspace, not a SaaS dashboard.
                        </h2>
                    </div>
                    <p className="text-ink-soft max-w-md">
                        ReachForge runs on your machine. No third-party storing
                        your lead data. No bulk sender between you and your
                        recipients.
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-10 items-start">
                    {/* Tab list */}
                    <div
                        role="tablist"
                        className="lg:col-span-4 border-t border-border"
                        data-testid="showcase-tablist"
                    >
                        {shots.map((s) => (
                            <button
                                key={s.id}
                                role="tab"
                                aria-selected={active === s.id}
                                data-testid={`showcase-tab-${s.id}`}
                                onClick={() => setActive(s.id)}
                                className={`w-full text-left flex items-center justify-between gap-4 px-4 py-5 border-b border-border transition-colors ${
                                    active === s.id
                                        ? "bg-bg"
                                        : "bg-transparent hover:bg-bg"
                                }`}
                            >
                                <div>
                                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-mute">
                                        {String(
                                            shots.findIndex(
                                                (x) => x.id === s.id,
                                            ) + 1,
                                        ).padStart(2, "0")}
                                    </div>
                                    <div
                                        className={`font-display font-semibold mt-1 ${
                                            active === s.id
                                                ? "text-ink"
                                                : "text-ink-soft"
                                        }`}
                                    >
                                        {s.label}
                                    </div>
                                </div>
                                <span
                                    className={`h-2 w-2 ${
                                        active === s.id
                                            ? "bg-forge"
                                            : "bg-border"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>

                    {/* Active panel */}
                    <div className="lg:col-span-8" data-testid="showcase-panel">
                        <div className="border border-ink bg-bg shadow-[8px_8px_0_0_hsl(var(--ink))]">
                            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-surface">
                                <span className="font-mono text-[11px] text-ink-mute">
                                    reachforge — {current.id}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setLightbox(current)}
                                    data-testid="showcase-enlarge"
                                    className="font-mono text-[11px] text-ink-mute hover:text-ink inline-flex items-center gap-1.5 transition-colors"
                                    aria-label={`Enlarge ${current.label}`}
                                >
                                    <Maximize2 size={11} strokeWidth={2.5} />
                                    <span className="uppercase tracking-[0.18em]">
                                        Enlarge
                                    </span>
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => setLightbox(current)}
                                data-testid="showcase-image-button"
                                className="block w-full text-left cursor-zoom-in group/sc relative"
                                aria-label={`Open ${current.label} in viewer`}
                            >
                                <img
                                    key={current.id}
                                    src={current.img}
                                    alt={current.label}
                                    className="block w-full h-auto fade-up"
                                    loading="lazy"
                                />
                                <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-ink/85 text-bg px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] opacity-0 group-hover/sc:opacity-100 transition-opacity">
                                    <Maximize2 size={11} strokeWidth={2.5} />
                                    Click to enlarge
                                </span>
                            </button>
                        </div>
                        <div className="mt-6 max-w-xl">
                            <h3 className="font-display text-2xl md:text-3xl font-semibold text-ink leading-tight">
                                {current.title}
                            </h3>
                            <p className="mt-3 text-ink-soft">
                                {current.desc}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${lightbox.label} — enlarged view`}
                    data-testid="showcase-lightbox"
                    onClick={() => setLightbox(null)}
                    className="fixed inset-0 z-[80] bg-ink/92 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 cursor-zoom-out fade-up"
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setLightbox(null);
                        }}
                        data-testid="showcase-lightbox-close"
                        aria-label="Close enlarged view"
                        className="absolute top-4 right-4 md:top-6 md:right-6 h-10 w-10 border border-bg/40 text-bg hover:bg-bg hover:text-ink transition-colors flex items-center justify-center"
                    >
                        <X size={18} />
                    </button>
                    <div
                        className="max-w-[1500px] w-full max-h-full flex flex-col gap-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-bg/60">
                            <span>
                                FIG.{shots.findIndex((x) => x.id === lightbox.id) + 3} —{" "}
                                {lightbox.label}
                            </span>
                            <span>Click anywhere to close · Esc</span>
                        </div>
                        <div className="border border-bg/30 bg-ink overflow-auto">
                            <img
                                src={lightbox.img}
                                alt={`${lightbox.label} — full size`}
                                onClick={() => setLightbox(null)}
                                className="block w-full h-auto cursor-zoom-out"
                            />
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
